import { getEnvVariable } from 'utils/common';

export const TPO_API_URL = getEnvVariable('TPO_API_URL') || 'https://applyapi.dev-lender.com/';
export const TPO_ACCOUNTS_API_BASE_URL =
  getEnvVariable('TPO_ACCOUNTS_API_BASE_URL') || 'https://accountsapi.dev-lender.com/';
export const TPO_SIGNING_API_BASE_URL =
  getEnvVariable('TPO_SIGNING_API_BASE_URL') || 'https://signingapi.dev-lender.com';
export const ADMIN_PANEL_URL = getEnvVariable('ADMIN_PANEL_URL') || 'https://admin.dev-lender.com';

/**
 * Hard ceiling on how long any single API request may stay in flight before
 * axios aborts it with `ECONNABORTED`. Without this a request whose connection
 * is silently dropped — e.g. the iframe session token rotates mid-transition
 * and the gateway never answers the in-flight step write — would leave its
 * promise unsettled forever, stranding the wizard's Continue button in a
 * permanent busy state. The abort turns that into a surfaced, retryable error.
 */
export const API_REQUEST_TIMEOUT_MS = 60_000;

/**
 * The post-OTP handoff provisions the whole broker in-request (DEV-4822) and
 * has been measured past 80s on dev, so the default ceiling aborted it while
 * the server kept going — the applicant was dropped to the start and then
 * locked out by "already registered" (STP-806). Sized well past the observed
 * worst case; a transient failure is still retried by the handoff loop.
 */
export const HANDOFF_REQUEST_TIMEOUT_MS = 180_000;

export const API_ENDPOINTS = {
  CONFIG: '/api/tpo-signup/config',
  CONFIG_PREVIEW: '/api/tpo-signup/config/preview',
  APPLICATIONS: '/api/tpo-signup/applications',
  OTP_SEND: '/api/tpo-signup/otp/send',
  OTP_VERIFY: '/api/tpo-signup/otp/verify',
  NMLS_COMPANY: '/api/tpo-signup/nmls/company',
  NMLS_INDIVIDUAL: '/api/tpo-signup/nmls/individual',
  NMLS_REGISTRATION_STATUS: '/api/tpo-signup/nmls/registration-status',
  RESUME_SEND_OTP: '/api/tpo-signup/resume/send-otp',
  RESUME_VERIFY: '/api/tpo-signup/resume/verify',
  ACCOUNT_EXECUTIVES: '/api/tpo-signup/account-executives',
  DOCUMENTS: '/api/tpo-signup/certifications/:id/documents',
  SIGNING: '/api/tpo-signup/applications/:id/signing',
  FORM_BUNDLE_SIGN: '/api/tpo-signup/applications/:id/form-bundle/sign',
  FORM_BUNDLE_STATUS: '/api/tpo-signup/applications/:id/form-bundle/status',
  FORM_BUNDLE_SIGNER_ELIGIBILITY: '/api/tpo-signup/applications/:id/form-bundle/signer-eligibility',
  CERTIFICATION_FORM_BUNDLE: '/api/tpo-signup/certifications/:certificationId/form-bundle',
  CERTIFICATION_FORM_BUNDLE_SIGN:
    '/api/tpo-signup/certifications/:certificationId/form-bundle/sign',
  CERTIFICATION_FORM_BUNDLE_STATUS:
    '/api/tpo-signup/certifications/:certificationId/form-bundle/status',
  CERTIFICATION_FORM_BUNDLE_SIGNER_ELIGIBILITY:
    '/api/tpo-signup/certifications/:certificationId/form-bundle/signer-eligibility',
  CERTIFICATION_FORM_BUNDLE_SIGNING_TOKEN:
    '/api/tpo-signup/certifications/:certificationId/form-bundle/signing-token',
  AUTHORIZED_SIGNERS: '/api/tpo-signup/applications/:id/authorized-signers',
  APPLICATION_SIGNERS: '/api/tpo-signup/applications/:id/signers',
  CERTIFICATION_APPLICATION_SIGNERS: '/api/tpo-signup/certifications/:certificationId/signers',
  CERTIFICATION_SIGNER_ROUTING: '/api/tpo-signup/certifications/:certificationId/signer-routing',
  CURRENT_USER: '/api/tpo-signup/current-user',
  CERTIFICATION_OWNER_LINK_CURRENT_USER:
    '/api/tpo-signup/certifications/:certificationId/owners/:ownerId/link-current-user',
  APPLICATION_COMPLETE: '/api/tpo-signup/:id/complete',
  APPLICATION_POST_OTP_HANDOFF: '/api/tpo-signup/:id/post-otp-handoff',
  IFRAME_SESSIONS: '/api/tpo-signup/iframe/sessions',
  CERTIFICATIONS: '/api/tpo-signup/certifications',
  STEPS_EVALUATE: '/api/tpo-signup/:configId/steps/evaluate',
};

/**
 * Stable code on the 409 body the signup endpoints return when the entered NMLS
 * already maps to an established SubCompany and a fresh registration is blocked
 * (DEV-3612). Mirrors the backend `TpoNmlsAlreadyRegisteredException.ErrorCode`.
 */
export const TPO_NMLS_ALREADY_REGISTERED_CODE = 'TPO_NMLS_ALREADY_REGISTERED';

/**
 * Stable code on the 409 body the OTP-verify endpoint returns when the code was
 * VALID but the sign-up it completes already exists — a race-loser, or a retry of
 * a request whose first attempt already provisioned the account (DEV-5610). Mirrors
 * the backend `TpoOtpController.SignupAlreadyCompletedCode`. Distinct from a bad/
 * expired code so the UI shows "already completed — sign in" rather than "re-enter".
 */
export const TPO_SIGNUP_ALREADY_COMPLETED_CODE = 'TPO_SIGNUP_ALREADY_COMPLETED';

/**
 * Stable code on the 409 body the OTP-verify endpoint returns when a NEW broker's
 * company name already belongs to a sub-company under this lender (STP-301). The
 * code is not consumed; the broker goes back to Company Details to change the name.
 * Mirrors the backend `TpoCompanyNameAlreadyRegisteredException.ErrorCode`.
 */
export const TPO_COMPANY_NAME_ALREADY_REGISTERED_CODE = 'TPO_COMPANY_NAME_ALREADY_REGISTERED';

/**
 * Stable code on the 409 body the OTP-verify endpoint returns when the account must set a
 * password first but none was sent (status changed after the code went out). The code is
 * spent, so the user must request a new one. Mirrors the backend
 * `TpoOtpController.PasswordSetupRequiredCode`.
 */
export const TPO_PASSWORD_SETUP_REQUIRED_CODE = 'TPO_PASSWORD_SETUP_REQUIRED';

/**
 * Stable code on the 409 body the OTP-send endpoint returns when a fresh signup (a new
 * email) supplies a phone number that already belongs to a different account (DEV-3808).
 * Accounts enforces email-OR-phone uniqueness, so the collision would otherwise surface as
 * a generic "email or phone already exists" failure. Mirrors the backend
 * `TpoPhoneAlreadyInUseException.ErrorCode`.
 */
export const TPO_PHONE_ALREADY_IN_USE_CODE = 'TPO_PHONE_ALREADY_IN_USE';
export const TPO_PHONE_INVALID_CODE = 'TPO_PHONE_INVALID';

export const TPO_ACCOUNT_INELIGIBLE_CODE = 'TPO_ACCOUNT_INELIGIBLE';

export const TPO_ACCOUNT_EXISTS_CONFLICT_CODE = 'TPO_ACCOUNT_EXISTS_CONFLICT';

/** URL prefix for all iframe-mode requests; used by the auth interceptor. */
export const CERTIFICATIONS_API_PREFIX = '/api/tpo-signup/certifications';

/**
 * Matches the certification document endpoint
 * `/api/tpo-signup/certifications/{certificationId}/documents`. A POST here is
 * a document upload, which the upload slots report inline with status-aware
 * copy (DEV-5582) — the response error interceptor skips its global
 * 403/5xx/network toasts for exactly that method+path so the partner never
 * sees two competing error surfaces. GETs on the same path (the documents
 * list) keep the global toasts.
 */
export const CERTIFICATION_DOCUMENTS_PATH_PATTERN =
  /^\/api\/tpo-signup\/certifications\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/documents\/?$/i;

/**
 * Matches the post-OTP handoff endpoint
 * `/api/tpo-signup/{applicationId}/post-otp-handoff`. Its failures are owned
 * by the "finishing your account" screen (STP-806), which retries them, so the
 * response error interceptor skips its global network/5xx toasts for it.
 */
export const POST_OTP_HANDOFF_PATH_PATTERN =
  /^\/api\/tpo-signup\/[0-9a-f-]{36}\/post-otp-handoff\/?$/i;

/**
 * Matches the dynamic step evaluator endpoint
 * `POST /api/tpo-signup/{configId}/steps/evaluate`, where `{configId}` is a
 * 36-character GUID. Used by the auth interceptor so the iframe session JWT
 * is also attached to this endpoint when the iframe drives the wizard.
 */
export const STEPS_EVALUATE_PATH_PATTERN =
  /^\/api\/tpo-signup\/[0-9a-f-]{36}\/steps\/evaluate(\b|\/|$)/i;

/**
 * Endpoints the cert iframe must reach with the in-memory session token even
 * though they don't live under `/certifications/*`: the current-user identity
 * probe and the "this owner is me" link (DEV-3691). The session token is the
 * only credential the iframe holds, so the auth interceptor attaches it here
 * too. `current-user` is exact; the link endpoint carries `{id}`/`{ownerId}`
 * GUID segments. The cert-scoped link variant
 * (`/certifications/{certificationId}/owners/{ownerId}/link-current-user`)
 * needs NO entry here: it already matches `CERTIFICATIONS_API_PREFIX`, so
 * `isCertificationsRequest` attaches the session token to it.
 */
export const IFRAME_AUTHED_PATH_PATTERNS: ReadonlyArray<RegExp> = [
  /^\/api\/tpo-signup\/current-user(\b|\/|$)/i,
  /^\/api\/tpo-signup\/applications\/[0-9a-f-]{36}\/owners\/[0-9a-f-]{36}\/link-current-user(\b|\/|$)/i,
];
